import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Press } from '@dorixona/ui'
import { withCountRu } from '@dorixona/utils'
import { Icon } from '../../ui/Icon'
import { Thumb } from '../../ui/Thumb'
import { Failed, failureText } from '../../ui/Load'
import { TableSkeleton } from '../../ui/Skeleton'
import { Pagination } from '../../ui/Pagination'
import { PageActions } from '../../app/PageHeader'
import { Select, type SelectOption } from '../../ui/Select'
import { useSession } from '../auth/session'
import { useCategories } from '../categories/api'
import { num, useSettings } from '../settings/api'
import { useBulkCategory, useProductCounts, useProducts, useSaveProduct, type Product } from './api'
import {
  EMPTY_FILTERS,
  PAGE_SIZES,
  filtersFromParams,
  filtersToParams,
  isFiltered,
  priceLabel,
  stockLabel,
  toQuery,
  type PageSize,
  type ProductFilters,
  type ProductState,
  type ProductType,
  type SortKey,
} from './model'

export interface ProductsScreenProps {
  onToast: (message: string) => void
}

const STATES: [ProductState, string][] = [
  ['all', 'Любой статус'],
  ['on', 'В приложении'],
  ['off', 'Скрытые'],
  ['zero', 'Нет в наличии'],
  ['nophoto', 'Без фото'],
  ['low', 'Заканчивается'],
]

const TYPES: SelectOption<ProductType>[] = [
  { value: 'all', label: 'Все типы' },
  { value: 'rx', label: 'Рецептурные' },
  { value: 'otc', label: 'Безрецептурные' },
]

const SORTS: [SortKey, string][] = [
  ['name', 'По названию'],
  ['price_asc', 'Цена: по возрастанию'],
  ['price_desc', 'Цена: по убыванию'],
  ['popular', 'Сначала популярные'],
]

/** Категория для выбранных ещё не выбрана — «Применить» недоступна. */
const NO_TARGET = 0
/** «Без категории»: сервер получает `category_id: null`. */
const NO_CATEGORY = -1

/**
 * Отмеченные строки. Держатся за адрес отбора: сменился фильтр, сортировка
 * или страница — отметки сбрасываются, на экране только текущая страница.
 */
interface Picked {
  key: string
  ids: number[]
  target: number
}

/**
 * Каталог: таблица товаров с поиском, фильтрами и сортировкой. Отбор делает
 * сервер — он знает остатки по всей сети и порог «заканчивается». Названия,
 * цены и остатки только читаются: ими владеет FOM (R-15).
 */
export function ProductsScreen({ onToast }: ProductsScreenProps) {
  const { can } = useSession()
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const filters = filtersFromParams(params)

  const settings = useSettings()
  const lowStock = num(settings.data ?? {}, 'catalog.low_stock_threshold', 25)
  const products = useProducts(toQuery(filters, lowStock))
  const categories = useCategories()
  const save = useSaveProduct()
  const bulk = useBulkCategory()

  // Число справа от каждого пункта фильтров — сколько товаров он покажет.
  // Пока числа не пришли, пункты просто без них.
  const counts = useProductCounts().data

  const canUpdate = can('products', 'u')
  // Колонка отметок — только у того, кто может проставить категорию.
  const columns = canUpdate ? 7 : 6

  const pageKey = params.toString()
  const [picked, setPicked] = useState<Picked>({ key: pageKey, ids: [], target: NO_TARGET })
  const pick: Picked =
    picked.key === pageKey ? picked : { key: pageKey, ids: [], target: NO_TARGET }

  const categoryOptions = [...(categories.data ?? [])]
    .sort((a, b) => a.sort - b.sort)
    .map((c) => ({ value: c.id, label: c.name }))
  const countedCategories = categoryOptions.map((option) => ({
    ...option,
    count: counts?.categories[option.value],
  }))

  /**
   * Новый отбор начинается с первой страницы. Листание — отдельный шаг в
   * истории браузера («Назад» возвращает на прежнюю страницу), а правка
   * фильтра заменяет текущий: иначе каждая буква поиска стала бы шагом.
   */
  function set(patch: Partial<ProductFilters>) {
    const paging = 'page' in patch
    setParams(filtersToParams({ ...filters, page: 1, ...patch }), { replace: !paging })
  }

  function toggleActive(product: Product, active: boolean) {
    save.mutate(
      { id: product.id, patch: { active } },
      {
        onSuccess: () => {
          onToast(active ? 'Товар показан в приложении' : 'Товар скрыт из приложения')
        },
        onError: (error) => {
          onToast(failureText(error))
        },
      },
    )
  }

  const filtered = isFiltered(filters)
  const pageIds = products.data?.items.map((p) => p.id) ?? []
  const allPicked = pageIds.length > 0 && pageIds.every((id) => pick.ids.includes(id))

  function togglePick(id: number, on: boolean) {
    setPicked({ ...pick, ids: on ? [...pick.ids, id] : pick.ids.filter((x) => x !== id) })
  }

  function unpick() {
    setPicked({ key: pageKey, ids: [], target: NO_TARGET })
  }

  function applyCategory() {
    bulk.mutate(
      { ids: pick.ids, categoryId: pick.target === NO_CATEGORY ? null : pick.target },
      {
        onSuccess: (updated) => {
          onToast(`Категория проставлена: ${withCountRu(updated, ['товар', 'товара', 'товаров'])}`)
          unpick()
        },
        onError: (error) => {
          onToast(failureText(error))
        },
      },
    )
  }

  function reset() {
    set({ ...EMPTY_FILTERS, sort: filters.sort, size: filters.size })
  }

  function goTo(page: number) {
    set({ page })
    // Новая страница читается сверху: таблица под фильтрами.
    window.scrollTo({ top: 0 })
  }

  return (
    <>
      <PageActions>
        {products.data ? (
          <span
            className="ui-count adm-count"
            aria-label={withCountRu(products.data.total, ['товар', 'товара', 'товаров'])}
          >
            {products.data.total}
          </span>
        ) : null}
      </PageActions>

      {/* Одна строка управления: поиск, фильтры и сортировка — одной формы и
          высоты. Таблица — отдельной карточкой под ней. */}
      <div className="ui-toolbar" role="search">
        <div className="ui-search">
          <Icon name="i-search" />
          <input
            className="ui-input"
            type="search"
            placeholder="Название или код FOM"
            aria-label="Поиск товара"
            value={filters.q}
            onChange={(e) => {
              set({ q: e.target.value })
            }}
          />
        </div>
        <Select
          label="Категория"
          value={filters.cat}
          active={filters.cat !== EMPTY_FILTERS.cat}
          options={[
            { value: 0, label: 'Все категории', count: counts?.states.all },
            { value: -1, label: 'Без категории', count: counts?.uncategorized },
            ...countedCategories,
          ]}
          onChange={(cat) => {
            set({ cat })
          }}
        />
        <Select
          label="Тип отпуска"
          value={filters.type}
          active={filters.type !== EMPTY_FILTERS.type}
          options={TYPES}
          onChange={(type) => {
            set({ type })
          }}
        />
        <Select
          label="Состояние"
          value={filters.state}
          active={filters.state !== EMPTY_FILTERS.state}
          options={STATES.map(([value, label]) => ({
            value,
            label,
            count: counts?.states[value],
          }))}
          onChange={(state) => {
            set({ state })
          }}
        />
        {filtered ? (
          <button type="button" className="ui-btn" data-variant="ghost" onClick={reset}>
            Сбросить фильтры
          </button>
        ) : null}
        <span className="spacer" />
        <Select
          label="Сортировка"
          lead="i-sort"
          align="end"
          value={filters.sort}
          options={SORTS.map(([value, label]) => ({ value, label }))}
          onChange={(sort) => {
            set({ sort })
          }}
        />
      </div>

      {/* Массовое действие — своей строкой над таблицей, пока есть отметки. */}
      {pick.ids.length > 0 ? (
        <div className="ui-toolbar" role="region" aria-label="Выбранные товары">
          <span className="pr-picked">Выбрано: {pick.ids.length}</span>
          <Select
            label="Новая категория"
            value={pick.target}
            options={[
              { value: NO_TARGET, label: 'Выберите категорию' },
              { value: NO_CATEGORY, label: 'Без категории' },
              ...categoryOptions,
            ]}
            onChange={(target) => {
              setPicked({ ...pick, target })
            }}
          />
          <Press
            as="button"
            type="button"
            className="ui-btn"
            data-variant="primary"
            squish="card"
            disabled={pick.target === NO_TARGET || bulk.isPending}
            aria-busy={bulk.isPending}
            onPress={applyCategory}
          >
            Применить
          </Press>
          <button type="button" className="ui-btn" data-variant="ghost" onClick={unpick}>
            Снять выбор
          </button>
        </div>
      ) : null}

      <section className="ui-table-card" aria-label="Каталог">
        {products.isPending ? <TableSkeleton what="каталог" /> : null}
        {products.isError ? (
          <Failed
            what="каталог"
            error={products.error}
            onRetry={() => {
              void products.refetch()
            }}
          />
        ) : null}

        {products.data ? (
          <>
            <div className="ui-table-wrap" aria-busy={products.isPlaceholderData}>
              <table className="ui-table ui-products">
                <colgroup>
                  {canUpdate ? <col className="w-pick" /> : null}
                  <col />
                  <col className="w-cat" />
                  <col className="w-price" />
                  <col className="w-stock" />
                  <col className="w-cb" />
                  <col className="w-on" />
                </colgroup>
                <thead>
                  <tr>
                    {canUpdate ? (
                      <th scope="col">
                        <label className="ui-check">
                          <input
                            type="checkbox"
                            aria-label="Выбрать все на странице"
                            checked={allPicked}
                            ref={(el) => {
                              if (el) el.indeterminate = !allPicked && pick.ids.length > 0
                            }}
                            onChange={() => {
                              setPicked({ ...pick, ids: allPicked ? [] : pageIds })
                            }}
                          />
                        </label>
                      </th>
                    ) : null}
                    <th scope="col">Товар</th>
                    <th scope="col">Категория</th>
                    <th scope="col" data-align="end">
                      Цена
                    </th>
                    <th scope="col" data-align="end">
                      Остаток
                    </th>
                    <th scope="col" data-align="end">
                      Кэшбэк
                    </th>
                    <th scope="col" data-align="center">
                      В приложении
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {products.data.items.length === 0 ? (
                    <tr>
                      <td colSpan={columns}>
                        <div className="ui-empty">
                          <Icon name="i-search" />
                          <b>Ничего не найдено</b>
                          <p>
                            {filters.page > 1
                              ? `На странице ${filters.page} товаров нет — список стал короче.`
                              : 'Измените запрос или сбросьте фильтры.'}
                          </p>
                          {filtered ? (
                            <button
                              type="button"
                              className="ui-btn"
                              data-size="sm"
                              data-variant="secondary"
                              onClick={reset}
                            >
                              Сбросить фильтры
                            </button>
                          ) : null}
                          {filters.page > 1 ? (
                            <button
                              type="button"
                              className="ui-btn"
                              data-size="sm"
                              data-variant="secondary"
                              onClick={() => {
                                goTo(1)
                              }}
                            >
                              К первой странице
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ) : null}
                  {products.data.items.map((product) => {
                    const href = `/products/${product.id}`
                    return (
                      <tr
                        key={product.id}
                        data-href={href}
                        onClick={() => {
                          void navigate(href)
                        }}
                      >
                        {canUpdate ? (
                          <td
                            onClick={(event) => {
                              event.stopPropagation()
                            }}
                          >
                            <label className="ui-check">
                              <input
                                type="checkbox"
                                aria-label={`Выбрать «${product.name}»`}
                                checked={pick.ids.includes(product.id)}
                                onChange={(e) => {
                                  togglePick(product.id, e.target.checked)
                                }}
                              />
                            </label>
                          </td>
                        ) : null}
                        <td>
                          <div className="ui-product">
                            <Thumb photos={product.photos} size={40} alt={product.name} />
                            <div className="txt">
                              <Link
                                to={href}
                                className="primary"
                                onClick={(event) => {
                                  event.stopPropagation()
                                }}
                              >
                                {product.name}
                              </Link>
                              {/* Производитель и метки — одной строкой: строки таблицы
                                  одной высоты, есть метки или нет. */}
                              <span className="meta">
                                <span className="maker">{product.maker}</span>
                                {product.rx ? (
                                  <span className="ui-tag" data-tone="rx">
                                    рецепт
                                  </span>
                                ) : null}
                                {product.photos.length === 0 ? (
                                  <span className="ui-tag" data-tone="warning">
                                    нет фото
                                  </span>
                                ) : null}
                                {product.popular ? <span className="ui-tag">витрина</span> : null}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td>
                          {product.categoryName || (
                            <span className="ui-tag" data-tone="warning">
                              без категории
                            </span>
                          )}
                        </td>
                        <td data-align="end">{priceLabel(product)}</td>
                        <td data-align="end">
                          {product.stockQty > 0 ? (
                            <span className="num">{stockLabel(product)}</span>
                          ) : (
                            <span className="ui-status">нет</span>
                          )}
                        </td>
                        {/* Процент категории — приглушён, свой — выделен: глаз
                            цепляется только за исключения. */}
                        <td data-align="end">
                          {product.cbOwn === null ? (
                            <span className="cb-inherited">
                              {product.cb}%<span className="visually-hidden"> из категории</span>
                            </span>
                          ) : (
                            <span className="cb-own">
                              <span className="ui-tag">свой</span>
                              {product.cb}%
                            </span>
                          )}
                        </td>
                        <td
                          data-align="center"
                          onClick={(event) => {
                            event.stopPropagation()
                          }}
                        >
                          {canUpdate ? (
                            <label className="ui-switch" data-size="sm">
                              <input
                                type="checkbox"
                                role="switch"
                                checked={product.active}
                                aria-label={`Показывать «${product.name}» в приложении`}
                                onChange={(e) => {
                                  toggleActive(product, e.target.checked)
                                }}
                              />
                            </label>
                          ) : product.active ? (
                            <span className="ui-status" data-tone="success">
                              показан
                            </span>
                          ) : (
                            <span className="ui-status">скрыт</span>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            <Pagination
              label="Страницы каталога"
              page={filters.page}
              pageSize={filters.size}
              total={products.data.total}
              sizes={PAGE_SIZES}
              busy={products.isPlaceholderData}
              onPage={goTo}
              onPageSize={(size) => {
                set({ size: size as PageSize })
              }}
            />
          </>
        ) : null}
      </section>
    </>
  )
}
